import { AxiosError, AxiosResponse } from 'axios';
import Confirm from 'components/shared/Confirm';
import { EditErrorOnSaveAlert } from 'components/shared/edit';
import { TextBox } from 'components/shared/edit/fields';
import ErrorAlert from 'components/shared/ErrorAlert';
import Spinner from 'components/shared/Spinner';
import Success from 'components/shared/Success';
import QRCode from 'qrcode.react';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { Link } from 'react-router-dom';
import NavigationPrompt from 'react-router-navigation-prompt';
import { Button } from 'reactstrap';
import AxiosHelper from 'services/AxiosHelper';
import { handleAxiosErrorWithValidation } from 'util/Misc';
import { WithTestId } from 'shared-client/types/test';
import entityDefinition from '../../Definition';
import ValidationErrorAlert, { AlertType } from '~/components/shared/edit/ValidationErrorAlert';

interface EnableAuthenticatorProps {
	afterSubmit: (generateCodes: boolean) => void;
	onCancelClick: () => void;
}

/**
 * Enable Authenticator DTO
 */
interface EnableAuthenticatorDto {
	sharedKey: string;
	authenticatorUri: string;
	code: string;
	statusMessage: string;
}

/**
 * Component that handles setting up a TOTP authenticator app (like Google Authenticator)
 */
function EnableAuthenticator(props: EnableAuthenticatorProps) {
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<AxiosError | null>(null);
	const [authenticatorValues, setAuthenticatorValues] = useState<EnableAuthenticatorDto>({
		authenticatorUri: '',
		code: '',
		sharedKey: '',
		statusMessage: '',
	});

	const testId = 'enable-authenticator';

	useEffect(() => {
		(async () => {
			// Load enable authenticator data
			await AxiosHelper.get(`${entityDefinition.baseAjaxRoute}/enable-totp-authenticator`)
				.then((resp: AxiosResponse<EnableAuthenticatorDto>) => {
					setAuthenticatorValues(resp.data);
					setIsLoading(false);
				})
				.catch((error: AxiosError) => {
					setError(error);
				});
		})();
	}, []);

	/**
	 * Save enable authenticator data
	 */
	const save = (formValues: any): Promise<void | Record<string, unknown>> => {
		const values = { ...formValues };

		// a promise must be returned for react-final-form.
		return AxiosHelper.post(`${entityDefinition.baseAjaxRoute}/enable-totp-authenticator`, values)
			.then((resp: AxiosResponse<any>) => {
				setError(null);
				props.afterSubmit(resp.data.generateCodes);
				return;
			})
			.catch((error: AxiosError) => {
				return handleAxiosErrorWithValidation(error);
			});
	};

	if (error) {
		return <ErrorAlert error={error} />;
	}

	if (isLoading) {
		return <Spinner />;
	}

	return (
		<>
			{authenticatorValues.statusMessage ? <Success message={authenticatorValues.statusMessage} /> : null}
			<h3>Enable Authenticator</h3>
			<div>
				<p>To use an authenticator app go through the following steps:</p>
				<ol className="list">
					<li>
						<p>
							Download a two-factor authenticator app like Microsoft Authenticator for{' '}
							<a href="https://go.microsoft.com/fwlink/?Linkid=825071">Windows Phone</a>,{' '}
							<a href="https://go.microsoft.com/fwlink/?Linkid=825072">Android</a> and{' '}
							<a href="https://go.microsoft.com/fwlink/?Linkid=825073">iOS</a> or Google Authenticator for{' '}
							<a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&amp;hl=en">
								Android
							</a>{' '}
							and <a href="https://itunes.apple.com/us/app/google-authenticator/id388497605?mt=8">iOS</a>.
						</p>
					</li>
					<li>
						<p>
							Scan the QR Code or enter this key{' '}
							<kbd data-testid={`${testId}-authenticator-key`}>{authenticatorValues.sharedKey}</kbd> into
							your two factor authenticator app. Spaces and casing do not matter.
						</p>
						<QRCode data-testid={`${testId}-qrcode`} value={authenticatorValues.authenticatorUri} />
					</li>
					<li>
						<p>
							Once you have scanned the QR code or input the key above, your two factor authentication app
							will provide you with a unique code. Enter the code in the confirmation box below.
						</p>
						<div className="row">
							<div className="col-md-11">
								<CodeEntryForm
									testId={testId}
									authenticatorValues={authenticatorValues}
									onSubmit={save}
									onCancelClick={props.onCancelClick}
								/>
							</div>
						</div>
					</li>
				</ol>
			</div>
		</>
	);
}

interface CodeEntryFormProps extends WithTestId {
	authenticatorValues: EnableAuthenticatorDto;
	onSubmit: (formValues: any) => void;
	onCancelClick: () => void;
}

/**
 * Form used to handle 2FA validation code entry
 */
function CodeEntryForm(props: CodeEntryFormProps) {
	return (
		<Form
			initialValues={props.authenticatorValues}
			onSubmit={props.onSubmit}
			subscription={{
				dirty: true,
				submitError: true,
				submitting: true,
				pristine: true,
			}}
			render={(formProps: any) => {
				const { handleSubmit, dirty, submitError, submitting } = formProps;

				return (
					<form onSubmit={handleSubmit} autoComplete="off">
						<NavigationPrompt when={dirty}>
							{({ isActive, onConfirm, onCancel }) => (
								<Confirm
									onCancel={onCancel}
									isOpen={isActive}
									onConfirm={onConfirm}
									renderTitle={() => 'Warning'}
									renderContents={() =>
										'Are you sure you want to leave this page? Your changes will be lost.'
									}
									confirmButtonText="Leave"
									cancelButtonText="Stay"
								/>
							)}
						</NavigationPrompt>

						<EditErrorOnSaveAlert />
						<ValidationErrorAlert submitError={submitError} alertType={AlertType.Edit} />
						<Field label="Code" name="code" component={TextBox} hideTooltip />
						<Link
							to="#"
							className="btn-width-medium btn btn-outline-primary-alt"
							onClick={props.onCancelClick}
							data-testid={`${props.testId}-cancel`}
						>
							Cancel
						</Link>
						{'  '}
						<Button
							type="submit"
							color="primary"
							disabled={submitting}
							className="btn-width-medium"
							data-testid={`${props.testId}-verify`}
						>
							Verify
						</Button>
					</form>
				);
			}}
		/>
	);
}

export default EnableAuthenticator;
